import type { RouteDetail, SuggestedTargets } from '@viaja/contracts';
import {
  Button,
  ChanceMeter,
  Chip,
  Icon,
  Mascot,
  Price,
  PriceCalendar,
  PriceChart,
  PriceLevel,
  PriceThermometer,
} from '@viaja/design-system';
import {
  MIN_RETURNS,
  belowTypicalPct,
  betterTarget,
  flexLabel,
  horizonLabel,
  priceCalendar,
} from '@viaja/domain';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { formatAdults, formatCheckedAt, formatDayShort, money, routeDates } from '../../lib/format';
import { useCheckRoute, useDeleteRoute, useRoute, useTrips, useUpdateRoute } from '../../lib/queries';
import { SubScreen } from '../../ui/AppShell';
import { Sheet } from '../../ui/Sheet';
import { TextField } from '../../ui/TextField';
import { LoadError, Loading, Notice } from '../../ui/feedback';

/** Painel da rota: Price + PriceLevel + PriceThermometer + ChanceMeter + PriceChart (ordem do DS). */
export function RoutePage() {
  const { id = '' } = useParams();
  const route = useRoute(id);

  return (
    <SubScreen title="Rota" back="/">
      {route.isPending ? (
        <Loading label="Buscando o preço…" />
      ) : route.error ? (
        <LoadError message={route.error.message} onRetry={() => route.refetch()} />
      ) : (
        <RoutePanel route={route.data} />
      )}
    </SubScreen>
  );
}

function RoutePanel({ route }: { route: RouteDetail }) {
  const navigate = useNavigate();
  const update = useUpdateRoute(route.id);
  const check = useCheckRoute(route.id);
  const remove = useDeleteRoute();
  const trips = useTrips();
  const [sheet, setSheet] = useState<'target' | 'trip' | 'delete' | null>(null);

  const { latest, stats, targetPrice: target } = route;
  const reached = Boolean(latest && target && latest.price <= target);
  const below = latest ? belowTypicalPct(latest.price, latest) : null;
  const trip = trips.data?.find((t) => t.id === route.tripId);
  const active = route.status === 'active';
  const suggested = route.suggestedTargets ?? null;
  const better =
    target && route.chance && suggested && !reached
      ? betterTarget({ target, chance: route.chance.chance }, suggested.items)
      : null;

  return (
    <>
      <div className="app-head">
        <h1 className="display-title">
          {route.origin.city} → {route.destination.city}
        </h1>
        <p className="small app-meta">
          {routeDates(route.outboundDate, route.returnDate)} · {formatAdults(route.adults)}
          {route.directOnly ? ' · só voos diretos' : ''}
        </p>
        {trip ? (
          <div className="app-wrap">
            <Chip icon="mala" onClick={() => navigate(`/viagens/${trip.id}`)}>
              {trip.name}
            </Chip>
          </div>
        ) : null}
      </div>

      {route.status === 'paused' ? (
        <Notice tone="neutral">Rota pausada. Não checo o preço enquanto ela estiver pausada.</Notice>
      ) : null}
      {route.status === 'expired' ? (
        <Notice tone="neutral">Essa data já passou. A rota fica aqui como registro.</Notice>
      ) : null}
      {route.lastError ? <Notice>{route.lastError}</Notice> : null}

      {latest ? (
        <section className="app-section" aria-label="Preço de hoje">
          <div className="app-price-head">
            <Price
              value={latest.price}
              {...(latest.deltaPct ? { delta: latest.deltaPct } : {})}
              deltaLabel="desde a última checagem"
            />
            <div className="app-wrap">
              {reached ? <PriceLevel level="target" /> : null}
              {below && latest.level === 'low' ? (
                <PriceLevel level="low">{below}% abaixo do típico</PriceLevel>
              ) : (
                <PriceLevel level={latest.level} />
              )}
            </div>
          </div>
          {stats ? (
            <PriceThermometer
              value={latest.price}
              min={Math.min(stats.min, latest.price, latest.typicalLow)}
              max={Math.max(stats.max, latest.price, latest.typicalHigh)}
              typicalLow={latest.typicalLow}
              typicalHigh={latest.typicalHigh}
            />
          ) : null}
        </section>
      ) : null}

      {route.advice ? (
        <section className="app-section" aria-labelledby="momento">
          <h2 id="momento" className="display-section">
            Comprar agora?
          </h2>
          <div className="app-stack app-stack--tight">
            <p className="body-strong">{route.advice.headline}</p>
            <ul className="app-reasons small">
              {route.advice.reasons.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
            <p className="caption app-meta">Leitura do histórico dessa rota, não uma garantia.</p>
          </div>
        </section>
      ) : null}

      <section className="app-section" aria-labelledby="alvo">
        <div className="app-section-head">
          <h2 id="alvo" className="display-section">
            Seu alvo
          </h2>
          {target ? (
            <Button variant="quiet" size="sm" onClick={() => setSheet('target')}>
              Mudar
            </Button>
          ) : null}
        </div>
        {!target ? (
          <div className="app-stack app-stack--tight">
            <p className="body app-meta">Diga quanto quer pagar e eu calculo a chance de chegar lá.</p>
            <div>
              <Button variant="secondary" size="sm" icon="alvo" onClick={() => setSheet('target')}>
                Definir alvo
              </Button>
            </div>
          </div>
        ) : reached ? (
          <div className="app-row">
            <p className="body">
              <strong>Seu alvo foi atingido.</strong> Você queria pagar até {money(target)}.
            </p>
            <Mascot pose="comemorar" size={96} label="" />
          </div>
        ) : (
          <ChanceMeter
            chance={route.chance?.chance ?? null}
            target={target}
            emptyText={chanceEmptyText(target, route.history.length)}
            {...(route.chance
              ? {
                  horizon: horizonLabel(route.chance.horizonDays),
                  confidence: route.chance.confidence,
                  basis: route.chance.basis,
                }
              : {})}
          />
        )}
        {better && suggested ? (
          <div className="app-stack app-stack--tight">
            <Notice tone="neutral">
              {money(better.target)} tem {better.chance}% de chance {horizonLabel(suggested.horizonDays)}. É o
              menor valor com boa chance de chegar.
            </Notice>
            <div>
              <Button
                variant="secondary"
                size="sm"
                icon="alvo"
                disabled={update.isPending}
                onClick={() => update.mutate({ targetPrice: better.target })}
              >
                Usar {money(better.target)}
              </Button>
            </div>
          </div>
        ) : null}
      </section>

      {route.history.length > 1 ? <ContextSection route={route} target={target} /> : null}

      {route.flexDays && route.status === 'active' ? <FlexSection route={route} /> : null}

      <section className="app-section" aria-label="Monitoramento">
        <div className="app-row">
          <p className="small app-meta">
            {route.latest ? `Checado ${formatCheckedAt(route.latest.checkedAt)}.` : 'Ainda não checado.'}
          </p>
          {active ? (
            <Button variant="quiet" size="sm" disabled={check.isPending} onClick={() => check.mutate()}>
              {check.isPending ? 'Checando…' : 'Checar agora'}
            </Button>
          ) : null}
        </div>
        {check.error ? <Notice>{check.error.message}</Notice> : null}
        {update.error ? <Notice>{update.error.message}</Notice> : null}
        <div className="app-wrap">
          {route.status !== 'expired' ? (
            <Chip
              selected={route.alertEnabled}
              icon="alerta"
              onClick={() => update.mutate({ alertEnabled: !route.alertEnabled })}
            >
              {route.alertEnabled ? 'Alerta ligado' : 'Alerta desligado'}
            </Chip>
          ) : null}
          {route.status !== 'expired' ? (
            <Chip
              icon={active ? 'pausar' : 'rota'}
              onClick={() => update.mutate({ status: active ? 'paused' : 'active' })}
            >
              {active ? 'Pausar rota' : 'Retomar rota'}
            </Chip>
          ) : null}
          {route.status !== 'expired' ? (
            <Chip selected={route.flexDays} onClick={() => update.mutate({ flexDays: !route.flexDays })}>
              ± 3 dias
            </Chip>
          ) : null}
          <Chip icon="mala" onClick={() => setSheet('trip')}>
            {trip ? 'Trocar viagem' : 'Pôr numa viagem'}
          </Chip>
          <Chip icon="fechar" onClick={() => setSheet('delete')}>
            Apagar
          </Chip>
        </div>
      </section>

      {route.bookingUrl ? (
        <div className="app-actionbar">
          <Button
            block
            iconRight="seta"
            onClick={() => window.open(route.bookingUrl ?? '', '_blank', 'noopener,noreferrer')}
          >
            Ver passagens
          </Button>
        </div>
      ) : null}

      <TargetSheet
        open={sheet === 'target'}
        current={target}
        price={latest?.price ?? null}
        suggested={suggested}
        pending={update.isPending}
        onClose={() => setSheet(null)}
        onSave={(value) => update.mutate({ targetPrice: value }, { onSuccess: () => setSheet(null) })}
      />
      <Sheet open={sheet === 'trip'} title="Viagem" onClose={() => setSheet(null)}>
        <div className="app-wrap">
          <Chip
            selected={!route.tripId}
            onClick={() => update.mutate({ tripId: null }, { onSuccess: () => setSheet(null) })}
          >
            Nenhuma
          </Chip>
          {(trips.data ?? []).map((t) => (
            <Chip
              key={t.id}
              icon="mala"
              selected={route.tripId === t.id}
              onClick={() => update.mutate({ tripId: t.id }, { onSuccess: () => setSheet(null) })}
            >
              {t.name}
            </Chip>
          ))}
        </div>
        {trips.data?.length === 0 ? (
          <p className="small app-meta">Você ainda não planejou nenhuma viagem.</p>
        ) : null}
      </Sheet>
      <Sheet open={sheet === 'delete'} title="Apagar esta rota?" onClose={() => setSheet(null)}>
        <p className="body">O histórico de preço dessa rota vai junto. Isso não dá para desfazer.</p>
        <Button
          block
          disabled={remove.isPending}
          onClick={() => remove.mutate(route.id, { onSuccess: () => navigate('/', { replace: true }) })}
        >
          Apagar rota
        </Button>
        <Button variant="secondary" block onClick={() => setSheet(null)}>
          Manter
        </Button>
      </Sheet>
    </>
  );
}

const TARGET_LABEL: Record<SuggestedTargets['items'][number]['kind'], string> = {
  provavel: 'Provável',
  bom_negocio: 'Bom negócio',
  ousado: 'Ousado',
};

/** Sem porcentagem ainda: mostra o alvo e diz o que falta, em vez de só "sem dados". */
function chanceEmptyText(target: number, days: number): string {
  const head = `Seu alvo é ${money(target)}.`;
  if (days < 2) return `${head} Ainda junto o histórico dessa busca para estimar a chance de chegar lá.`;
  const missing = Math.max(1, MIN_RETURNS + 1 - days);
  return `${head} Tenho ${days} dias de histórico. Com mais ${missing}, estimo a chance de chegar lá.`;
}

/** Datas vizinhas ± 3 dias (WP-03) e calendário de calor (WP-04). */
function FlexSection({ route }: { route: RouteDetail }) {
  const flex = route.flex;
  const best = flex?.options.find((o) => o.offset === flex.bestOffset) ?? null;
  const months = flex
    ? priceCalendar(
        [
          { date: route.outboundDate, price: flex.basePrice },
          ...flex.options.map((o) => ({ date: o.outboundDate, price: o.price })),
        ],
        route.outboundDate,
      )
    : [];
  return (
    <section className="app-section" aria-labelledby="vizinhas">
      <h2 id="vizinhas" className="display-section">
        Datas vizinhas
      </h2>
      {!flex ? (
        <p className="small app-meta">
          Comparo 3 dias antes e depois, com a mesma duração de viagem, uma vez por semana e só quando sobra
          cota de buscas. A primeira comparação sai em breve.
        </p>
      ) : (
        <div className="app-stack app-stack--tight">
          {best ? (
            <p className="body">
              <strong>
                {flexLabel(best.offset)}: {money(best.price)} (−{Math.abs(best.deltaPct)}%)
              </strong>
              <br />
              <span className="small app-meta">{routeDates(best.outboundDate, best.returnDate)}</span>
            </p>
          ) : (
            <p className="body">Nenhuma data vizinha sai mais barata que a sua.</p>
          )}
          {months.map((m) => (
            <PriceCalendar
              key={m.key}
              month={m.label}
              startWeekday={m.startWeekday}
              days={m.days}
              note={`Data de ida · ${formatAdults(route.adults)}, em R$`}
            />
          ))}
          <p className="caption app-meta">
            Comparado {formatCheckedAt(flex.checkedAt)}. A data da rota não muda sozinha.
          </p>
        </div>
      )}
    </section>
  );
}

/** Janelas do gráfico: o DS pede de 30 a 90 dias (PriceChart.md). */
const CHART_WINDOWS = [30, 90] as const;

function ContextSection({ route, target }: { route: RouteDetail; target: number | null }) {
  const [windowDays, setWindowDays] = useState<(typeof CHART_WINDOWS)[number]>(90);
  const { latest, stats, allTimeStats: all } = route;
  const points = route.history.slice(-windowDays);
  const longer = all && stats && all.days > stats.days;
  return (
    <section className="app-section" aria-labelledby="contexto">
      <h2 id="contexto" className="display-section">
        O preço no contexto
      </h2>
      {route.history.length > 30 ? (
        <div className="app-wrap">
          {CHART_WINDOWS.map((d) => (
            <Chip key={d} selected={windowDays === d} onClick={() => setWindowDays(d)}>
              Últimos {d} dias
            </Chip>
          ))}
        </div>
      ) : null}
      <PriceChart
        points={points.map((p, i, arr) => ({
          label: i === arr.length - 1 ? 'Hoje' : `${arr.length - 1 - i} dias atrás`,
          price: p.price,
        }))}
        {...(latest ? { typicalLow: latest.typicalLow, typicalHigh: latest.typicalHigh } : {})}
        {...(target && chartShowsTarget(target, stats) ? { target } : {})}
      />
      {stats ? (
        <div className="app-stats">
          <Stat label="Menor" value={stats.min} />
          <Stat label="Média" value={stats.avg} />
          <Stat label="Maior" value={stats.max} />
        </div>
      ) : null}
      {stats ? <p className="caption app-meta">Últimos {stats.days} dias dessa mesma busca.</p> : null}
      {longer ? (
        <p className="caption app-meta">
          Desde {formatDayShort(all.since)}, em {all.days} dias: menor {money(all.min)}, média{' '}
          {money(all.avg)}, maior {money(all.max)}.
        </p>
      ) : null}
    </section>
  );
}

/** Alvo muito longe do histórico achataria a linha do preço; nesse caso ele fica só no "Seu alvo". */
function chartShowsTarget(target: number, stats: RouteDetail['stats']): boolean {
  if (!stats) return true;
  return target >= stats.min * 0.5 && target <= stats.max * 1.5;
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="app-stat">
      <span className="caption app-meta">{label}</span>
      <span className="price-sm">{money(value)}</span>
    </div>
  );
}

function TargetSheet({
  open,
  current,
  price,
  suggested,
  pending,
  onClose,
  onSave,
}: {
  open: boolean;
  current: number | null;
  price: number | null;
  suggested: SuggestedTargets | null;
  pending: boolean;
  onClose: () => void;
  onSave: (value: number | null) => void;
}) {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string>();
  const fmt = (n: number) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  return (
    <Sheet
      open={open}
      title="Seu alvo"
      onClose={() => {
        setError(undefined);
        onClose();
      }}
    >
      {suggested && suggested.items.length > 0 ? (
        <fieldset className="app-fieldset app-stack app-stack--tight">
          <legend className="eyebrow app-meta">Sugestões pelo histórico</legend>
          <div className="app-wrap">
            {suggested.items.map((item) => (
              <Chip
                key={item.kind}
                icon="alvo"
                selected={current === item.target}
                disabled={pending}
                onClick={() => onSave(item.target)}
              >
                {TARGET_LABEL[item.kind]}: {money(item.target)} · {item.chance}%
              </Chip>
            ))}
          </div>
          <p className="caption app-meta">
            Chance de chegar ao valor {horizonLabel(suggested.horizonDays)}, com confiança{' '}
            {suggested.confidence}.
          </p>
        </fieldset>
      ) : null}
      <TextField
        label="Quanto você quer pagar?"
        prefix="R$"
        inputMode="numeric"
        placeholder={current ? fmt(current) : price ? fmt(Math.round(price * 0.85)) : ''}
        value={value}
        onChange={(e) => setValue(e.target.value.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, '.'))}
        error={error}
        hint={price ? `Hoje está ${money(price)}.` : undefined}
        data-autofocus
      />
      <Button
        block
        icon="alvo"
        disabled={pending}
        onClick={() => {
          const n = Number(value.replace(/\D/g, ''));
          if (!n) return setError('Digite um valor em reais.');
          setError(undefined);
          setValue('');
          onSave(n);
        }}
      >
        Salvar alvo
      </Button>
      {current ? (
        <Button variant="quiet" block onClick={() => onSave(null)}>
          Tirar o alvo
        </Button>
      ) : null}
      <p className="caption app-meta">
        <Icon name="alerta" size={16} /> A chance é uma estimativa pelo histórico, não uma garantia.
      </p>
    </Sheet>
  );
}
